

/** Votes */

import { atom } from "jotai";
import { VotePayload } from "../models/vote.model";

import { store } from './atom.service';
import axiosService from "./axios.service";
import { convertDataToIdeaPayload } from "./idea.service";
import { convertDataToHackathonPayload } from "./hackathon.service";
import { getNotifications } from "./notification.service";

// the user's own votes, keyed by voteKey(ideaId, sessionId)
export const votesAtom = atom<Record<string, VotePayload>>({});

export const voteKey = (ideaId: string, sessionId?: string | null) => `${ideaId}:${sessionId || ''}`;


const convertDataToVote = (data: any, sessionKey?: string | null): VotePayload => {
    const payload: VotePayload = {
        _id: data._id,
        userId: data.userId,
        ideaId: data.ideaId,
        session: data.session || null,
        scores: data.scores,
        notes: data.notes || [],
        feedback: data.feedback || '',
        answered: data.answered,
        categoryIds: data.categoryIds,
        saved: data.saved
    }

    const mergedPayload = payload;

    store.set(votesAtom, votes => ({
        ...votes,
        [voteKey(data.ideaId, sessionKey !== undefined ? sessionKey : data.session)]: mergedPayload
    }));

    return mergedPayload;
}

// returns a vote and an idea
// categoryIds: the category ids the scores are in order of; the server rejects the vote (409) if
// categories changed since the page loaded, rather than saving scores against the wrong category
// sessionId: the judging session the vote is for (required when the team presents in several)
export const submitVote = async (hackathonId: string, ideaId: string, scores: number[], notes: string[] = [], categoryIds?: string[], sessionId?: string, feedback?: string) => {
    // ensure scores and notes do not have null
    scores = scores.map(score => score || 0);
    notes = notes.map(note => note || '');
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/ideas/${ideaId}/vote`, { scores, notes, categoryIds, session: sessionId, feedback });
    const idea = convertDataToIdeaPayload(data.idea);
    const vote = convertDataToVote(data.vote, sessionId ?? null);
    // scoring the team on stage marks its "Now presenting" read on the server; let the bell's count follow
    window.setTimeout(() => getNotifications().catch(() => { }), 800);
    return { idea, vote };
}

export const getVote = async (hackathonId: string, ideaId: string, sessionId?: string) => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/ideas/${ideaId}/vote`, {
        params: sessionId ? { session: sessionId } : undefined
    });

    return convertDataToVote(data, sessionId ?? null);
}

export { isSavedVote } from '../utils/votes';

// the teams (and rooms) the signed-in user has scored in a hackathon, as voteKeys; no scores
export const getMyVotes = async (hackathonId: string): Promise<string[]> => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/myvotes`);
    return (data as Array<{ ideaId: string, session: string | null }>).map(v => voteKey(v.ideaId, v.session));
}

// the judges' feedback for a team: its members get it once results are announced, organizers any time
export const getTeamFeedback = async (hackathonId: string, ideaId: string): Promise<string[]> => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/ideas/${ideaId}/feedback`);
    return data.feedback || [];
}

// the organizer's "Last call": tells everyone with teams left to score which ones; returns how many were told
export const sendLastCall = async (hackathonId: string): Promise<number> => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/lastcall`);
    return data.notified;
}

export type JudgeProgress = { _id: string, username: string, scored: number, total: number };

// participants who score: how many have scored every team they can, and how many have started
export type ParticipantProgress = { done: number, started: number, total: number };

// organizers: how far the judges and participants have got (counts only)
export const getJudgingProgress = async (hackathonId: string): Promise<{ judges: JudgeProgress[], participants: ParticipantProgress | null }> => {
    const { data } = await axiosService.get(`/api/hackathons/${hackathonId}/judging/progress`);
    return { judges: data.judges || [], participants: data.participants || null };
}

export const aggregateVotes = async (hackathonId: string) => {
    const { data } = await axiosService.post(`/api/hackathons/${hackathonId}/aggregate_results`);
    return convertDataToHackathonPayload(data);
}
